import {useState, useEffect, useRef, useCallback, useReducer} from '@wordpress/element';
import {useElements, useStripe} from "@stripe/react-stripe-js";
import {ensureErrorResponse, ensureSuccessResponse, getBillingDetailsFromAddress} from '@wc-stripe/blocks-utils';
import {
    reducer,
    useEvents,
    emitFilter,
    constants
} from './actions';

export const useCreatePaymentMethod = (
    {
        name,
        gatewayId,
        emitResponse,
        billingAddress,
        shippingAddress,
        eventRegistration,
        shouldSavePayment,
        activePaymentMethod,
        shouldCreatePaymentMethod = true
    }) => {
    const [paymentMethodType, setPaymentMethodType] = useState('');
    const [paymentMethodData, _setPaymentMethodData] = useState(null);
    const {onPaymentSetup} = eventRegistration;
    const stripe = useStripe();
    const elements = useElements();

    const [eventCallbacks, dispatch] = useReducer(reducer, {});
    const events = useEvents(dispatch);

    const currentData = useRef({
        billingAddress,
        shippingAddress,
        paymentMethodData: {},
        eventCallbacks,
        activePaymentMethod,
        shouldCreatePaymentMethod
    });

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            billingAddress,
            eventCallbacks,
            paymentMethodType,
            paymentMethodData,
            activePaymentMethod,
            shouldCreatePaymentMethod
        };
    }, [
        eventCallbacks,
        billingAddress,
        shippingAddress,
        paymentMethodData,
        paymentMethodType,
        activePaymentMethod,
        shouldCreatePaymentMethod
    ]);

    const setPaymentMethodData = useCallback((value) => {
        _setPaymentMethodData(prev => ({...prev, ...value}));
    }, []);

    const clearPaymentMethodData = useCallback(() => {
        _setPaymentMethodData(null);
    }, []);

    const createPaymentMethod = useCallback(async () => {
        const {billingAddress, eventCallbacks, shouldCreatePaymentMethod} = currentData.current;
        try {
            let result = await elements.submit();
            if (result.error) {
                throw result.error;
            }

            if (shouldCreatePaymentMethod) {
                const data = await emitFilter({
                    elements,
                    params: {
                        billing_details: {
                            phone: '',
                            email: '',
                            ...getBillingDetailsFromAddress(billingAddress)
                        }
                    }
                }, null, {events: eventCallbacks, type: constants.CREATE_PAYMENT_DATA});

                result = await stripe.createPaymentMethod(data);

                if (result.error) {
                    throw result.error;
                }
                setPaymentMethodData(result.paymentMethod);

                return result.paymentMethod;
            }
            return {type: result.selectedPaymentMethod};
        } catch (error) {
            clearPaymentMethodData();
            throw error;
        }
    }, [
        stripe,
        elements,
        setPaymentMethodData,
        clearPaymentMethodData
    ]);

    useEffect(() => {
        const unsubscribe = onPaymentSetup((async () => {
            if (activePaymentMethod !== name) {
                return null;
            }
            const {
                eventCallbacks,
                billingAddress,
                shippingAddress,
                paymentMethodType,
                paymentMethodData
            } = currentData.current;
            try {
                let paymentMethod = paymentMethodData;
                if (!paymentMethod?.id) {
                    paymentMethod = await createPaymentMethod();
                }
                const data = await emitFilter(
                    {
                        meta: {
                            paymentMethodData: {
                                [`${gatewayId}_payment_method_id`]: paymentMethod.id || '',
                                [`wc-${gatewayId}-new-payment-method`]: shouldSavePayment,
                                _stripe_payment_method_type: paymentMethod.type || paymentMethodType
                            }
                        }
                    },
                    {paymentMethod, billingAddress, shippingAddress},
                    {events: eventCallbacks, type: constants.ADD_SUCCESS_DATA}
                );

                return ensureSuccessResponse(emitResponse.responseTypes, data);
            } catch (error) {
                return ensureErrorResponse(
                    emitResponse.responseTypes,
                    error,
                    {
                        messageContext: emitResponse.noticeContexts.PAYMENTS
                    }
                );
            }
        }));
        return () => {
            unsubscribe();
        };
    }, [
        name,
        stripe,
        elements,
        gatewayId,
        onPaymentSetup,
        shouldSavePayment,
        createPaymentMethod,
        activePaymentMethod
    ]);

    return {
        events,
        paymentMethodType,
        paymentMethodData,
        createPaymentMethod,
        setPaymentMethodData,
        setPaymentMethodType,
        clearPaymentMethodData
    }
}